import React, { useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { dictionary } from '../../../dictionary';

const InvoiceReportDialog = ({
  visible,
  onHide,
  data,
  allInvoices,
  filteredInvoices,
  allPosts,
  currentGJ,
  dateRange,
  onCreateGoogleSheet,
}) => {
  const [maximized, setMaximized] = useState(false);

  const normalizeYear = (year) => {
    const y = parseInt(year, 10);
    if (isNaN(y)) return new Date().getFullYear();
    return y < 100 ? 2000 + y : y;
  };

  const formatValue = (val, type = 'PLN') => {
    const num = Number(val);
    if (isNaN(num)) return '-';
    return (
      num.toLocaleString('pl-PL', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) + (type === 'PLN' ? ' PLN' : ' €')
    );
  };

  const reportData = useMemo(() => {
    if (!data || !allInvoices || !Array.isArray(allInvoices)) return [];

    const targetGJ = normalizeYear(currentGJ);

    const calculateGJ = (dateInput) => {
      if (!dateInput) return null;
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return null;
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      return month >= 3 ? year : year - 1;
    };

    const isInvInTargetGJ = (inv, tgj) => {
      return calculateGJ(inv.dateInvoice1) === tgj || calculateGJ(inv.dateInvoice2) === tgj;
    };

    const getEntryProp = (inv, prop) => (inv.entries && inv.entries.length > 0 ? inv.entries[0][prop] : 'Brak');

    const formatAsArray = (obj, valFormatter = (v) => v, keyFormatter = (k) => k) => {
      const keys = Object.keys(obj);
      if (keys.length === 0) return 'Brak';
      return keys.map((k) => ({ label: keyFormatter(k), value: valFormatter(obj[k]) }));
    };

    const logIdsInView = new Set(data.flatMap((group) => (group.entries ? group.entries.map((e) => e._id) : [])));
    const invoicesSource = filteredInvoices || allInvoices;
    const invoicesInView = invoicesSource.filter(
      (inv) => inv.entries && inv.entries.some((entry) => logIdsInView.has(entry._id))
    );

    const totalPaidFilteredNetto = invoicesInView.reduce((acc, inv) => {
      if (inv.invoiceStatus === 'withdrawn') return acc;
      return acc + (Number(inv.netPaid1Invoice) || 0) + (Number(inv.netPaid2Invoice) || 0);
    }, 0);

    const totalPaidFilteredBrutto = invoicesInView.reduce((acc, inv) => {
      if (inv.invoiceStatus === 'withdrawn') return acc;
      return acc + (Number(inv.grossPaid1Invoice) || 0) + (Number(inv.grossPaid2Invoice) || 0);
    }, 0);

    // Oblicz wartość "brutto weryf" jako netto * 1.23
    const totalPaidFilteredBruttoCheck = totalPaidFilteredNetto * 1.23;

    const sumEspCostNetFiltered = invoicesInView.reduce((acc, inv) => {
      if (inv.invoiceStatus === 'withdrawn') return acc;
      return acc + (Number(inv.espCostNet) || 0);
    }, 0);

    const sumEspCostGrossFiltered = invoicesInView.reduce((acc, inv) => {
      if (inv.invoiceStatus === 'withdrawn') return acc;
      return acc + (Number(inv.espCostGross) || 0);
    }, 0);

    const avgPriceFiltered =
      invoicesInView.length > 0
        ? invoicesInView.reduce((acc, inv) => acc + (Number(inv.espCostNet) || 0), 0) / invoicesInView.length
        : 0;

    const countStagesArr = (list) => {
      const stages = {};
      let total = 0;
      list.forEach((inv) => {
        let s = getEntryProp(inv, 'stage');
        if (s === 'Sign Off') s = 'Sign_off';
        stages[s] = (stages[s] || 0) + 1;
        total++;
      });
      const sortedKeys = Object.keys(stages).sort((a, b) => a.localeCompare(b));
      if (sortedKeys.length === 0) return '0';
      const result = sortedKeys.map((k) => ({ label: k, value: stages[k] }));
      result.push({ label: 'SUM', value: total });
      return result;
    };

    const espStats = invoicesInView.reduce((acc, inv) => {
      const esp = getEntryProp(inv, 'esp');
      if (!acc[esp]) acc[esp] = { count: 0, paid: 0, payable: 0 };
      acc[esp].count++;
      acc[esp].paid += (Number(inv.netPaid1Invoice) || 0) + (Number(inv.netPaid2Invoice) || 0);
      acc[esp].payable += Number(inv.netPayable) || 0;
      return acc;
    }, {});

    const openModulesStats = {};
    let openModulesTotal = 0;
    invoicesInView.forEach((inv) => {
      if (inv.invoiceStatus === 'incomplete' || inv.invoiceStatus === 'in-progress') {
        const gj = calculateGJ(inv.dateInvoice1) || 'Brak';
        openModulesStats[gj] = (openModulesStats[gj] || 0) + 1;
        openModulesTotal++;
      }
    });
    const openModulesArr = formatAsArray(
      openModulesStats,
      (v) => v,
      (k) => `GJ ${k}`
    );
    if (Array.isArray(openModulesArr)) openModulesArr.push({ label: 'SUM', value: openModulesTotal });

    let eldSignOff = 0;
    let espSignOff = 0;

    const parseGJ = (val) => {
      if (!val || val === '-') return null;
      const y = parseInt(val, 10);
      if (isNaN(y)) return null;
      return y < 100 ? 2000 + y : y;
    };

    const uniqueEspModules = new Set();

    if (data && Array.isArray(data)) {
      data.forEach((post) => {
        if (!post.entries) return;

        const signOffEntries = post.entries.filter((e) => e.stage === 'Sign_off' || e.stage === 'Sign Off');

        if (signOffEntries.length > 0) {
          let matchesTargetGJ = false;

          const invoiceGJ = parseGJ(post._computedGJ);

          if (invoiceGJ !== null) {
            if (invoiceGJ === targetGJ) {
              matchesTargetGJ = true;
            }
          } else {
            matchesTargetGJ = signOffEntries.some((e) => parseGJ(calculateGJ(e.date)) === targetGJ);
          }

          if (matchesTargetGJ) {
            const moduleName = post.module ? post.module.trim() : 'brak';
            const countryName = post.country ? post.country.trim() : 'brak';
            const langName = post.lang ? post.lang.trim() : 'brak';

            const uniqueKey = `${moduleName}_${countryName}_${langName}`;
            uniqueEspModules.add(uniqueKey);
          }
        }
      });

      espSignOff = uniqueEspModules.size;
    }

    if (allPosts && Array.isArray(allPosts)) {
      let filteredEld = allPosts;

      if (dateRange && dateRange[0]) {
        const start = new Date(dateRange[0]);
        start.setHours(0, 0, 0, 0);
        const end = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
        end.setHours(23, 59, 59, 999);

        filteredEld = filteredEld.filter((post) => {
          if (!post.date) return false;
          const postDate = new Date(post.date);
          return postDate >= start && postDate <= end;
        });
      }

      const uniqueEldModules = new Set();

      filteredEld.forEach((post) => {
        const clientLower = post.client ? post.client.toLowerCase().trim() : '';
        const isClientCountry = clientLower === 'countries' || clientLower === 'country';
        const hasSignOff = post.stage === 'Sign_off' || post.stage === 'Sign Off';
        const entryGJ = parseGJ(calculateGJ(post.date));
        const isTargetGJ = entryGJ === targetGJ;

        if (isClientCountry && hasSignOff && isTargetGJ) {
          const moduleName = post.module ? post.module.trim() : 'brak';
          const countryName = post.country ? post.country.trim() : 'brak';
          const langName = post.lang ? post.lang.trim() : 'brak';

          const uniqueKey = `${moduleName}_${countryName}_${langName}`;
          uniqueEldModules.add(uniqueKey);
        }
      });

      eldSignOff = uniqueEldModules.size;
    }

    const signOffArr = [
      { label: 'ELD', value: eldSignOff },
      { label: 'ESP', value: espSignOff },
      { label: 'SUM', value: eldSignOff + espSignOff },
    ];

    const countriesEsp = invoicesInView.reduce((acc, inv) => {
      const esp = getEntryProp(inv, 'esp');
      const cty = getEntryProp(inv, 'country');
      if (!acc[esp]) acc[esp] = new Set();
      acc[esp].add(cty);
      return acc;
    }, {});
    const typesObj = invoicesInView.reduce((acc, inv) => {
      const t = getEntryProp(inv, 'module_type');
      acc[t] = (acc[t] || 0) + 1;
      return acc;
    }, {});
    const modulesObj = invoicesInView.reduce((acc, inv) => {
      const m = getEntryProp(inv, 'module');
      acc[m] = (acc[m] || 0) + 1;
      return acc;
    }, {});
    const countriesObj = invoicesInView.reduce((acc, inv) => {
      const c = getEntryProp(inv, 'country');
      acc[c] = (acc[c] || 0) + 1;
      return acc;
    }, {});

    const allInvoicesTargetGJ = allInvoices.filter((inv) => isInvInTargetGJ(inv, targetGJ));

    let sumPaidGJ = 0;
    let sumPayableGJ = 0;
    allInvoices.forEach((inv) => {
      if (inv.invoiceStatus !== 'withdrawn') {
        // if (calculateGJ(inv.dateInvoice1) === targetGJ) {
        //           sumPaidGJ += Number(inv.netPaid1Invoice) || 0;
        //           sumPayableGJ += Number(inv.netPayable) || 0;
        //         }
        //         if (calculateGJ(inv.dateInvoice2) === targetGJ) {
        //           sumPaidGJ += Number(inv.netPaid2Invoice) || 0;
        //         }

        // ZMIANA do tego co wyżej jest zakomentowane: Spójność z księgowością w globalnym exporcie
        if (calculateGJ(inv.payment1Confirmed) === targetGJ) {
          sumPaidGJ += Number(inv.netPaid1Invoice) || 0;
        }
        if (calculateGJ(inv.payment2Confirmed) === targetGJ) {
          sumPaidGJ += Number(inv.netPaid2Invoice) || 0;
        }

        // Suma Payable wciąż na bazie faktury
        if (calculateGJ(inv.dateInvoice1) === targetGJ) {
          sumPayableGJ += Number(inv.netPayable) || 0;
        }
        //Koniec zmiany
      }
    });

    const avgPriceGJ =
      allInvoicesTargetGJ.length > 0
        ? allInvoicesTargetGJ.reduce((acc, inv) => acc + (Number(inv.espCostNet) || 0), 0) / allInvoicesTargetGJ.length
        : 0;

    const firstInvStats = {};
    let firstInvTotal = 0;
    allInvoices.forEach((inv) => {
      if (Number(inv.netPaid1Invoice) > 0 && (!inv.netPaid2Invoice || Number(inv.netPaid2Invoice) === 0)) {
        const gj = calculateGJ(inv.dateInvoice1) || 'Brak';
        firstInvStats[gj] = (firstInvStats[gj] || 0) + 1;
        firstInvTotal++;
      }
    });
    const firstInvArr = formatAsArray(
      firstInvStats,
      (v) => v,
      (k) => `GJ ${k}`
    );
    if (Array.isArray(firstInvArr)) firstInvArr.push({ label: 'SUM', value: firstInvTotal });

    return [
      {
        id: 1,
        name: dictionary.esp_report_item_1,
        value: formatValue(sumPaidGJ),
        desc: dictionary.esp_report_desc_1.replace('{gj}', targetGJ),
        isFilterDependent: false,
      },
      {
        id: 2,
        name: dictionary.esp_report_item_2,
        value: [
          { label: 'netto', value: formatValue(totalPaidFilteredNetto) },
          { label: 'brutto', value: formatValue(totalPaidFilteredBrutto) },
          { label: 'brutto check (netto*123%)', value: formatValue(totalPaidFilteredBruttoCheck) },
        ],
        desc: dictionary.esp_report_desc_2,
        isFilterDependent: true,
      },
      {
        id: 3,
        name: dictionary.esp_report_item_3,
        value: formatValue(sumEspCostNetFiltered),
        desc: dictionary.esp_report_desc_3,
        isFilterDependent: true,
      },
      {
        id: 4,
        name: dictionary.esp_report_item_4,
        value: formatValue(sumEspCostGrossFiltered),
        desc: dictionary.esp_report_desc_4,
        isFilterDependent: true,
      },
      {
        id: 5,
        name: dictionary.esp_report_item_5,
        value: formatValue(sumPayableGJ),
        desc: dictionary.esp_report_desc_5.replace('{gj}', targetGJ),
        isFilterDependent: false,
      },
      {
        id: 6,
        name: dictionary.esp_report_item_6,
        value: formatValue(avgPriceFiltered),
        desc: dictionary.esp_report_desc_6,
        isFilterDependent: true,
      },
      {
        id: 7,
        name: dictionary.esp_report_item_7,
        value: formatValue(avgPriceGJ),
        desc: dictionary.esp_report_desc_7.replace('{gj}', targetGJ),
        isFilterDependent: false,
      },
      {
        id: 8,
        name: dictionary.esp_report_item_8,
        value: formatValue(
          invoicesInView.reduce((acc, inv) => acc + (Number(inv.conv) || 0), 0) / (invoicesInView.length || 1)
        ),
        desc: dictionary.esp_report_desc_8,
        isFilterDependent: true,
      },
      {
        id: 9,
        name: dictionary.esp_report_item_9,
        value: countStagesArr(invoicesInView),
        desc: dictionary.esp_report_desc_9,
        isFilterDependent: true,
      },
      {
        id: 10,
        name: dictionary.esp_report_item_10,
        value: countStagesArr(allInvoicesTargetGJ),
        desc: dictionary.esp_report_desc_10.replace('{gj}', targetGJ),
        isFilterDependent: false,
      },
      {
        id: 11,
        name: dictionary.esp_report_item_11,
        value: invoicesInView.filter((i) => i.invoiceStatus === 'complete').length,
        desc: dictionary.esp_report_desc_11,
        isFilterDependent: true,
      },
      {
        id: 12,
        name: dictionary.esp_report_item_12,
        value: allInvoicesTargetGJ.filter((i) => i.invoiceStatus === 'complete').length,
        desc: dictionary.esp_report_desc_12.replace('{gj}', targetGJ),
        isFilterDependent: false,
      },
      {
        id: 13,
        name: dictionary.esp_report_item_13,
        value: invoicesInView.filter((i) => i.invoiceStatus === 'withdrawn').length,
        desc: dictionary.esp_report_desc_13,
        isFilterDependent: true,
      },
      {
        id: 14,
        name: dictionary.esp_report_item_14,
        value: allInvoicesTargetGJ.filter((i) => i.invoiceStatus === 'withdrawn').length,
        desc: dictionary.esp_report_desc_14.replace('{gj}', targetGJ),
        isFilterDependent: false,
      },
      {
        id: 15,
        name: dictionary.esp_report_item_15,
        value: formatAsArray(espStats, (v) => v.count),
        desc: dictionary.esp_report_desc_15,
        isFilterDependent: true,
      },
      {
        id: 16,
        name: dictionary.esp_report_item_16,
        value: formatAsArray(countriesObj),
        desc: dictionary.esp_report_desc_16,
        isFilterDependent: true,
      },
      {
        id: 17,
        name: dictionary.esp_report_item_17,
        value: formatAsArray(espStats, (v) => formatValue(v.paid)),
        desc: dictionary.esp_report_desc_17,
        isFilterDependent: true,
      },
      {
        id: 18,
        name: dictionary.esp_report_item_18,
        value: formatAsArray(espStats, (v) => formatValue(v.payable)),
        desc: dictionary.esp_report_desc_18,
        isFilterDependent: true,
      },
      {
        id: 19,
        name: dictionary.esp_report_item_19,
        value: firstInvArr,
        desc: dictionary.esp_report_desc_19,
        isFilterDependent: false,
      },
      {
        id: 20,
        name: dictionary.esp_report_item_20,
        value: openModulesArr,
        desc: dictionary.esp_report_desc_20,
        isFilterDependent: true,
      },
      {
        id: 21,
        name: dictionary.esp_report_item_21,
        value: formatAsArray(countriesEsp, (v) => [...v].join(', ')),
        desc: dictionary.esp_report_desc_21,
        isFilterDependent: true,
      },
      {
        id: 22,
        name: dictionary.esp_report_item_22,
        value: formatAsArray(typesObj),
        desc: dictionary.esp_report_desc_22,
        isFilterDependent: true,
      },
      {
        id: 23,
        name: dictionary.esp_report_item_23,
        value: formatAsArray(modulesObj),
        desc: dictionary.esp_report_desc_23,
        isFilterDependent: true,
      },
      {
        id: 24,
        name: dictionary.esp_report_item_24,
        value: signOffArr,
        desc: dictionary.esp_report_desc_24,
        isFilterDependent: true,
      },
      {
        id: 25,
        name: dictionary.esp_report_item_25,
        value: invoicesInView.filter((inv) => inv.invoiceStatus === 'complete').length,
        desc: dictionary.esp_report_desc_25,
        isFilterDependent: true,
      },
    ];
  }, [data, allInvoices, filteredInvoices, allPosts, currentGJ, dateRange]);

  const headerElement = (
    <div className="esp-report-header-wrapper">
      <span className="font-bold">{dictionary.esp_report_header}</span>
    </div>
  );

  const footer = (
    <div>
      <Button
        label={dictionary.esp_report_export}
        icon="pi pi-external-link"
        onClick={() => onCreateGoogleSheet(reportData)}
        className="p-button-success"
      />
    </div>
  );

  const valueBodyTemplate = (rowData) => {
    if (Array.isArray(rowData.value)) {
      return (
        <table className="esp-nested-table">
          <tbody>
            {rowData.value.map((row, idx) => (
              <tr
                key={idx}
                style={row.label === 'SUM' ? { borderTop: '2px solid var(--border-color)', fontWeight: 'bold' } : {}}
              >
                <td className="nested-label">{row.label}</td>
                <td className="nested-value">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    return <span style={{ fontWeight: 'bold', color: 'var(--primary-color)' }}>{rowData.value}</span>;
  };

  const rowClassName = (rowData) => {
    return {
      'row-no-filter': !rowData.isFilterDependent,
    };
  };

  return (
    <Dialog
      header={headerElement}
      visible={visible}
      onHide={onHide}
      footer={footer}
      maximizable
      maximized={maximized}
      onMaximize={(e) => setMaximized(e.maximized)}
      className="esp-report-dialog"
      breakpoints={{ '960px': '95vw' }}
    >
      <DataTable
        value={reportData}
        size="small"
        stripedRows
        showGridlines
        emptyMessage={dictionary.esp_report_empty}
        rowClassName={rowClassName}
      >
        <Column field="id" header="Lp." className="esp-report-col-id" />
        <Column field="name" header={dictionary.esp_report_pos_name} className="esp-report-col-name" />
        <Column
          field="value"
          header={dictionary.esp_report_value}
          body={valueBodyTemplate}
          className="esp-report-col-value"
        />
        <Column field="desc" header={dictionary.esp_report_desc_comm} className="esp-report-col-desc" />
      </DataTable>
    </Dialog>
  );
};

export default InvoiceReportDialog;
